/**
 * Profile review — the gate before a subject's social media screening.
 *
 * The backend queues the social media profiles it could not vouch for as
 * `profile_reviews` rows; a reviewer (an admin or any user on the
 * requesting team) confirms or rejects each one and submits, which
 * releases the confirmed profiles to the screening order and lets the
 * report continue.
 */

import type { ProfileReviewMode, ReportStatus } from './report';

/** Ferretly platform slug. Known values: `linkedin`, `twitter`,
 * `facebook`, `instagram`, `tiktok`, `youtube`, `reddit`, `pinterest`;
 * unknown ones are rendered plainly rather than rejected. */
export type ProfilePlatform = string;

/** Where the profile came from. */
export type ProfileSource =
  | 'client'
  | 'analyst'
  | 'research'
  | 'osint-industries'
  | 'profile-finder';

/** Why the row is in the queue. */
export type ProfileQueueReason =
  | 'system_ordered'
  | 'finder_candidate'
  | 'osint_unvouched_name'
  | 'osint_other_name_contact'
  | 'reviewer_added';

export type ProfileStatus = 'pending' | 'confirmed' | 'rejected' | 'superseded';

/** The reviewer's verdict on a row; `null` on a pending row before submit. */
export type ProfileDecision = 'confirm' | 'reject';

/** The deployment-wide setting: `shadow` records decisions without
 * holding the screening. Shown to admins only. */
export type ProfileReviewSetting = 'off' | 'shadow' | 'enforce';

/** One finder evidence entry; `kind` names the signal, the rest varies
 * by kind (a `snippet` and/or a `url` are the common extras). An
 * `archive_history` entry carries the web archive's record of the profile
 * URL instead: ISO timestamps of the first and last capture (null when
 * there is none) and the number of captures. */
export interface FinderEvidenceEntry {
  kind: string;
  snippet?: string;
  url?: string;
  first_capture?: string | null;
  last_capture?: string | null;
  captures?: number;
  [key: string]: unknown;
}

/** Evidence of a profile-finder row. */
export interface FinderProfileEvidence {
  evidence: FinderEvidenceEntry[];
  confidence?: number;
}

/** Another account the OSINT lookup found linked to or from this one, as
 * the backend builds it (`digital_footprint_service`). */
export interface OsintLinkedProfile {
  platform?: string | null;
  username?: string | null;
  profile_url?: string | null;
}

/** Evidence of an OSINT Industries row. Never an email address or a
 * phone number: the backend strips the selector value. `linked_profiles`
 * entries are objects on the wire; a bare URL string is accepted too. */
export interface OsintProfileEvidence {
  selector_type?: string;
  name_on_account?: string | null;
  verified?: boolean;
  linked_profiles?: (OsintLinkedProfile | string)[];
  module?: string;
}

/** Whatever evidence the source left; the card narrows it to one of the
 * known shapes at render time and shows nothing for the rest. */
export type ProfileEvidence = Record<string, unknown>;

export interface ApiSubjectProfile {
  id: string;
  report_id: string;
  subject_id: string;
  platform: ProfilePlatform;
  username: string | null;
  profile_url: string;
  source: ProfileSource;
  queue_reason: ProfileQueueReason;
  system_reason: string | null;
  confidence: number | null;
  evidence: ProfileEvidence | null;
  status: ProfileStatus;
  late: boolean;
  decision: ProfileDecision | null;
  decided_by_user_id: string | null;
  decided_at: string | null;
  note: string | null;
  created_at: string | null;
}

export interface SubjectProfile {
  id: string;
  reportId: string;
  subjectId: string;
  platform: ProfilePlatform;
  username: string | null;
  profileUrl: string;
  source: ProfileSource;
  queueReason: ProfileQueueReason;
  /** The system's own account of why the row is queued, verbatim. */
  systemReason: string | null;
  /** Probability that the profile is the subject's, when the source has one. */
  confidence: number | null;
  evidence: ProfileEvidence;
  status: ProfileStatus;
  /** A confirmation recorded after the subject's order went out; nothing
   * is re-ordered. */
  late: boolean;
  decision: ProfileDecision | null;
  decidedByUserId: string | null;
  decidedAt: string | null;
  note: string | null;
  createdAt: string | null;
}

export interface ApiSubjectProfileReviews {
  subject_id: string;
  subject_name: string;
  order_placed: boolean;
  profiles: ApiSubjectProfile[];
}

export interface ApiProfileReviewList {
  report_id: string;
  report_status: ReportStatus;
  mode: ProfileReviewMode;
  setting: ProfileReviewSetting;
  pending_count: number;
  undecided_count: number;
  subjects: ApiSubjectProfileReviews[];
}

export interface SubjectProfileReviews {
  subjectId: string;
  subjectName: string;
  /** The subject's screening order has already gone out; later
   * confirmations are recorded only. */
  orderPlaced: boolean;
  profiles: SubjectProfile[];
}

export interface ProfileReviewList {
  reportId: string;
  reportStatus: ReportStatus;
  mode: ProfileReviewMode;
  setting: ProfileReviewSetting;
  /** Rows still gating the report (status `pending`). */
  pendingCount: number;
  /** Pending rows without a verdict yet — submit is blocked while > 0. */
  undecidedCount: number;
  subjects: SubjectProfileReviews[];
}

export interface ApiProfileReviewSubmitResponse {
  next_action: string | null;
  report_status: ReportStatus;
  advanced_subject_ids: string[];
  late_subject_ids: string[];
}

export interface ProfileReviewSubmitResponse {
  nextAction: string | null;
  reportStatus: ReportStatus;
  /** Subjects whose screening order went out on this submit. */
  advancedSubjectIds: string[];
  /** Subjects whose order had already gone out; the confirmations were
   * recorded only. */
  lateSubjectIds: string[];
}

export function transformToSubjectProfile(api: ApiSubjectProfile): SubjectProfile {
  return {
    id: api.id,
    reportId: api.report_id,
    subjectId: api.subject_id,
    platform: api.platform,
    username: api.username ?? null,
    profileUrl: api.profile_url,
    source: api.source,
    queueReason: api.queue_reason,
    systemReason: api.system_reason ?? null,
    confidence: api.confidence ?? null,
    evidence: api.evidence ?? {},
    status: api.status,
    late: api.late ?? false,
    decision: api.decision ?? null,
    decidedByUserId: api.decided_by_user_id ?? null,
    decidedAt: api.decided_at ?? null,
    note: api.note ?? null,
    createdAt: api.created_at ?? null,
  };
}

export function transformToProfileReviewList(api: ApiProfileReviewList): ProfileReviewList {
  return {
    reportId: api.report_id,
    reportStatus: api.report_status,
    mode: api.mode,
    setting: api.setting,
    pendingCount: api.pending_count,
    undecidedCount: api.undecided_count,
    subjects: api.subjects.map((s) => ({
      subjectId: s.subject_id,
      subjectName: s.subject_name,
      orderPlaced: s.order_placed ?? false,
      profiles: s.profiles.map(transformToSubjectProfile),
    })),
  };
}

export function transformToProfileReviewSubmitResponse(
  api: ApiProfileReviewSubmitResponse
): ProfileReviewSubmitResponse {
  return {
    nextAction: api.next_action ?? null,
    reportStatus: api.report_status,
    advancedSubjectIds: api.advanced_subject_ids ?? [],
    lateSubjectIds: api.late_subject_ids ?? [],
  };
}
